// Koppelingen · Klantplannen (KoppelingenPlannen): elk Planner-klantplan hoort bij één Clockify-project, meestal
// "EdiFlex" onder de Clockify-klant van die klant. Daarbinnen zoekt de app de Clockify-taken voor de taken van het plan;
// zonder project kan geen enkele taak van dat plan gekoppeld worden. Plannen zonder project staan bovenaan, met het
// beste voorstel op naam (zekerheid van de server); in de lade kiest u zelf uit alle Clockify-projecten, per klant.
import { useState } from "react";
import { clockifyApi, fmtHours, useClockifyLive, type ClockifyLinks, type PlanLinkRow } from "../lib/api-clockify";
import type { PlanningData } from "../lib/types";
import { ClockifyNotice, KoppelCard, KoppelList, OK, WARN, pctOf, scorePill, useKoppelSave, type KRow } from "./KoppelList";
import { KoppelPicker, type PickOpt } from "./KoppelPicker";

const nOf = (x: number, one: string, many: string) => x + " " + (x === 1 ? one : many);
/** "AIT (Altrea International Transport) · EdiFlex", of enkel de projectnaam zonder Clockify-klant */
export const projText = (p: { name: string; client: string }) => (p.client ? p.client + " · " : "") + p.name;
const NONE = "__geen";

/** Clockify-project -> het klantplan dat het al heeft (behalve dit plan) */
function takenBy(links: ClockifyLinks, except?: string): Map<string, string> {
  return new Map(links.plans.filter((p) => p.linked && p.projectId !== except).map((p) => [p.linked!.clockifyProjectId, p.klant]));
}

export function KoppelPlannen({ q, links, data }: { q: string; links: ClockifyLinks; data: PlanningData }) {
  const live = useClockifyLive();
  const { save, busy } = useKoppelSave();
  const [pick, setPick] = useState<string | null>(null);
  const klanten = new Map(data.klanten.map((k) => [k.id, k]));
  const open = new Map<string, number>(), linkedN = new Map<string, number>();
  for (const t of data.tasks) open.set(t.projectId, (open.get(t.projectId) || 0) + 1);
  for (const r of links.tasks) if (r.linked) linkedN.set(r.projectId, (linkedN.get(r.projectId) || 0) + 1);
  const taken = takenBy(links);
  const planName = (p: PlanLinkRow) => klanten.get(p.projectId)?.plan || "Planner " + p.klant;

  const link = (p: PlanLinkRow, cid: string) => save(() => clockifyApi.linkPlan(p.projectId, cid), () => clockifyApi.linkPlan(p.projectId, p.linked?.clockifyProjectId || ""));

  const qq = q.trim().toLowerCase();
  const plans = links.plans
    .filter((p) => !qq || [planName(p), p.klant, p.linked ? projText(p.linked) : "", ...p.suggestions.map(projText)].join(" ").toLowerCase().includes(qq))
    .sort((a, b) => Number(!!a.linked) - Number(!!b.linked) || a.klant.localeCompare(b.klant));
  const rows: KRow[] = plans.map((p) => {
    const base = { key: p.projectId, a: planName(p), aSub: nOf(open.get(p.projectId) || 0, "open taak", "open taken") + " · " + (linkedN.get(p.projectId) || 0) + " gekoppeld" };
    if (p.linked) {
      const h = live.data?.plans[p.projectId];
      return {
        ...base, b: projText(p.linked), bCls: "mono",
        bSub: h ? fmtHours(h.logged) + " gelogd in Clockify" + (h.estimate ? " · " + fmtHours(h.estimate) + " geschat" : "") : "gekoppeld door " + p.linked.by,
        pill: "Gekoppeld", pillCls: OK,
        acts: [{ label: "Wijzigen", run: () => setPick(p.projectId) }, { label: "Ontkoppelen", title: "De taken van dit plan wachten dan weer op een project", run: () => link(p, "") }],
      };
    }
    const sug = p.suggestions.find((s) => !taken.has(s.clockifyProjectId));
    if (sug) {
      const pc = pctOf(sug.score), more = p.suggestions.length - 1;
      return {
        ...base, b: projText(sug), bCls: "mono", bSub: "voorstel op naam" + (more > 0 ? " · nog " + nOf(more, "ander voorstel", "andere voorstellen") : ""),
        pill: pc + " %", pillCls: scorePill(pc),
        acts: [{ label: "Bevestigen", primary: true, run: () => link(p, sug.clockifyProjectId) }, { label: "Ander project", run: () => setPick(p.projectId) }],
      };
    }
    return {
      ...base, b: "Geen voorstel", bSub: p.suggestions.length ? "de voorstellen horen al bij een ander klantplan" : "kies het Clockify-project van deze klant",
      pill: "Kiezen", pillCls: WARN, acts: [{ label: "Project kiezen", primary: true, run: () => setPick(p.projectId) }],
    };
  });
  const picked = pick ? links.plans.find((p) => p.projectId === pick) : null;

  return (
    <>
      <KoppelCard title="Klantplannen en Clockify-projecten" sub="Eén project per klantplan · daarbinnen zoekt de app de Clockify-taken voor de taken van dat plan"
        notice={<ClockifyNotice state={links.clockify.state} error={links.clockify.error} />}
        foot="De app bewaart elke koppeling zelf; alleen gekoppelde taken tellen mee in uren en bezetting.">
        <KoppelList rows={rows} busy={busy} empty={qq ? "Niets gevonden voor " + q.trim() + "." : "Er zijn geen klantplannen."} />
      </KoppelCard>
      {picked && <PlanPicker key={picked.projectId} plan={picked} links={links} data={data} onClose={() => setPick(null)} />}
    </>
  );
}

/** Zelf het Clockify-project van een klantplan kiezen: voorstellen eerst, dan alle projecten per Clockify-klant. */
export function PlanPicker({ plan, links, data, onClose }: { plan: PlanLinkRow; links: ClockifyLinks; data: PlanningData; onClose: () => void }) {
  const { save, busy } = useKoppelSave();
  const cur = plan.linked?.clockifyProjectId || null;
  const taken = takenBy(links, plan.projectId);
  const k = data.klanten.find((x) => x.id === plan.projectId);
  const opts: PickOpt[] = [], seen = new Set<string>();
  const add = (o: PickOpt) => { if (!seen.has(o.key)) { seen.add(o.key); opts.push(o); } };
  if (plan.linked) add({ key: plan.linked.clockifyProjectId, label: projText(plan.linked), note: "nu gekoppeld aan dit klantplan", pill: "Gekoppeld", pillCls: OK, group: "Nu gekoppeld" });
  for (const s of plan.suggestions) {
    const pc = pctOf(s.score), by = taken.get(s.clockifyProjectId);
    add({ key: s.clockifyProjectId, label: projText(s), note: by ? "hoort al bij klantplan " + by : "voorstel op naam", pill: pc + " %", pillCls: scorePill(pc), disabled: !!by, group: "Voorstellen op naam" });
  }
  const all = [...links.projects].sort((a, b) => (a.client || "￿").localeCompare(b.client || "￿") || a.name.localeCompare(b.name));
  for (const p of all) {
    const by = taken.get(p.clockifyProjectId);
    add({ key: p.clockifyProjectId, label: p.name, note: by ? "hoort al bij klantplan " + by : undefined, disabled: !!by, group: p.client || "Zonder klant in Clockify" });
  }
  if (cur) add({ key: NONE, label: "Geen Clockify-project", note: "loskoppelen: de taken van dit plan wachten dan weer", keep: true, group: "Loskoppelen" });
  const first = plan.suggestions.find((s) => !taken.has(s.clockifyProjectId));

  const confirm = (sel: string | null) => {
    if (!sel || sel === cur) { onClose(); return; }
    const to = sel === NONE ? "" : sel;
    save(() => clockifyApi.linkPlan(plan.projectId, to), () => clockifyApi.linkPlan(plan.projectId, cur || "")).then((ok) => { if (ok) onClose(); });
  };
  return (
    <KoppelPicker eyebrow="Klantplan koppelen" title={k?.plan || "Planner " + plan.klant}
      state={plan.linked ? ["Gekoppeld aan " + projText(plan.linked), OK] : ["Nog niet gekoppeld", WARN]}
      kv={[
        ["Klant", plan.klant],
        ["Open taken", String(data.tasks.filter((t) => t.projectId === plan.projectId).length)],
        ["Gekoppelde taken", String(links.tasks.filter((r) => r.projectId === plan.projectId && r.linked).length)],
      ]}
      listTitle="Kies een Clockify-project" options={opts} initial={cur || first?.clockifyProjectId || null}
      note={(links.projects.length ? "" : "Er zijn nog geen Clockify-projecten gelezen. ") + "Projecten die al bij een ander klantplan horen, kunt u niet kiezen. Gearchiveerde projecten staan er niet bij."}
      confirmText={(sel) => (sel === NONE ? "Loskoppelen" : "Koppelen")} busy={busy} onConfirm={confirm} onClose={onClose} />
  );
}

